import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import styles from "./WebsitePage.module.css";

import appRight from "../assets/journeysim2.png";
import groupSessionsImg from "../assets/darkmodegroupcopy.png";
import flashcardsImg from "../assets/flash2.png";
import faviconIcon from "../assets/favicondr.png";
import linkedinIcon from "../assets/linkedinicon.png";
import appStoreBtn from "../assets/appstorebutton.png";
import playStoreBtn from "../assets/playstorebutton.png";
import matwLogo from "../assets/MATW.png";

const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    visible: { opacity: 1, y: 0 },
};

const fadeUpDelayed = (delay = 0) => ({
    hidden: { opacity: 0, y: 32 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] } },
});

const LAST_UPDATED_PRIVACY = "2026-01-12";
const LAST_UPDATED_TERMS   = "2026-01-12";

const IOS_URL     = 'https://apps.apple.com/app/deenroute/id6757677330';
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.deenroute.app';

function useSeo({ title, description }) {
    useEffect(() => {
        if (title) document.title = title;
        if (typeof document === "undefined") return;
        if (description) {
            let tag = document.querySelector('meta[name="description"]');
            if (!tag) {
                tag = document.createElement("meta");
                tag.setAttribute("name", "description");
                document.head.appendChild(tag);
            }
            tag.setAttribute("content", description);
        }
    }, [title, description]);
}

/* ── Scroll indicator arrow ──────────────────────── */
const ScrollArrow = () => (
    <motion.div
        className={styles.scrollArrow}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4, duration: 0.6 }}
    >
        <motion.span
            animate={{ y: [0, 7, 0] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
            aria-hidden="true"
        >
            ↓
        </motion.span>
    </motion.div>
);

/* ── Floating app store badge ─────────────────────── */
const FloatingStoreBadge = () => {
    const [visible, setVisible] = useState(true);
    const debounceRef = useRef(null);

    useEffect(() => {
        const showAfterDelay = () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => setVisible(true), 1000);
        };
        const onScroll = () => { setVisible(false); showAfterDelay(); };
        window.addEventListener('scroll',    onScroll, { passive: true });
        window.addEventListener('touchmove', onScroll, { passive: true });
        window.addEventListener('wheel',     onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll',    onScroll);
            window.removeEventListener('touchmove', onScroll);
            window.removeEventListener('wheel',     onScroll);
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const wrapperClass = `${styles.floatingStore} ${visible ? styles.floatingVisible : styles.floatingHidden}`;

    return (
        <div className={wrapperClass} role="region" aria-label="Download DeenRoute" aria-hidden={!visible}>
            <div className={styles.floatingInner}>
                <a href={IOS_URL} target="_blank" rel="noopener noreferrer" className={styles.storeLink}>
                    <img src={appStoreBtn} alt="Download on the App Store" className={styles.storeBtn} />
                </a>
                <a href={ANDROID_URL} target="_blank" rel="noopener noreferrer" className={styles.storeLink}>
                    <img src={playStoreBtn} alt="Get it on Google Play" className={styles.storeBtn} />
                </a>
            </div>
        </div>
    );
};

/* ── Legal page wrapper ────────────────────────────── */
const LegalPageShell = ({ children, title, lastUpdated }) => (
    <main className={styles.content}>
        <article className={styles.legalArticle} aria-labelledby="legal-title">
            <header className={styles.legalHeader}>
                <h1 id="legal-title" className={styles.legalTitle}>{title}</h1>
                <p className={styles.legalUpdated}>Last updated: {lastUpdated}</p>
            </header>
            <div className={styles.legalBody}>{children}</div>
        </article>
    </main>
);

/* ── Privacy content ─────────────────────────────── */
const PrivacyPolicyContent = () => (
    <>
        <p className={styles.legalIntro}>
            This Privacy Policy explains how DeenRoute ("DeenRoute", "we", "us") collects, uses,
            shares, and protects your information when you use our website and app.
        </p>
        <h2 className={styles.legalH2}>Who we are</h2>
        <p>
            DeenRoute is a modern, beginner-friendly educational platform designed to support Muslims (including
            reverts) in learning and reviewing Islamic knowledge through interactive experiences.
        </p>
        <p>
            DeenRoute is operated by <strong>DeenRoute Ltd</strong>, a company registered in England and Wales.
        </p>
        <h2 className={styles.legalH2}>What data we collect</h2>
        <ul className={styles.legalList}>
            <li><strong>Account information:</strong> name and email address.</li>
            <li><strong>Learning progress and scores:</strong> activity completion, quiz results, streaks, and similar progress signals.</li>
            <li><strong>Device/app information:</strong> basic diagnostic logs such as device type, app version, timestamps, and error reports.</li>
            <li><strong>Subscription/payment information:</strong> subscription status and entitlement details. Payments are handled by app stores and/or payment providers (e.g., Apple, Google, PayPal, Paystack). We do not store your full card details.</li>
            <li><strong>Support communications:</strong> emails or messages you send to us (including attachments you provide).</li>
        </ul>
        <h2 className={styles.legalH2}>What we don't collect or store</h2>
        <ul className={styles.legalList}>
            <li><strong>Passwords in plaintext:</strong> we never store passwords in plaintext. If DeenRoute uses passwords, we store only securely hashed passwords. If DeenRoute uses passwordless login, we don't store a password at all.</li>
        </ul>
        <h2 className={styles.legalH2}>Why we collect this data</h2>
        <ul className={styles.legalList}>
            <li><strong>Login and account management</strong> (e.g., create an account, sign in, recover access).</li>
            <li><strong>Sync progress</strong> across devices and sessions.</li>
            <li><strong>Leaderboards</strong> (if enabled) and community features.</li>
            <li><strong>Fraud prevention and security</strong>, including detecting abuse and protecting accounts.</li>
            <li><strong>Customer support</strong> and responding to requests.</li>
        </ul>
        <h2 className={styles.legalH2}>Legal basis / consent</h2>
        <p>We process data to provide the core functionality of DeenRoute (such as login and syncing progress). Where required, we rely on your consent (for example, optional analytics or marketing communications). You can withdraw consent where applicable by adjusting settings or contacting us.</p>
        <h2 className={styles.legalH2}>Data retention</h2>
        <ul className={styles.legalList}>
            <li><strong>Account and learning data:</strong> retained while your account is active and until you request deletion.</li>
            <li><strong>Support communications:</strong> kept as long as needed to address your request and for reasonable record-keeping.</li>
            <li><strong>Diagnostic logs:</strong> typically retained for up to <strong>30 days</strong> (or longer if needed for security and debugging).</li>
        </ul>
        <h2 className={styles.legalH2}>Sharing &amp; subprocessors</h2>
        <p>We may share data with service providers (subprocessors) that help us operate and improve DeenRoute. We share only what's necessary for them to perform their services.</p>
        <ul className={styles.legalList}>
            <li><strong>Infrastructure / hosting:</strong> e.g., cloud hosting providers.</li>
            <li><strong>Auth &amp; databases:</strong> e.g., Firebase, MongoDB.</li>
            <li><strong>Analytics / crash reporting:</strong> e.g., basic diagnostics tooling, PostHog.</li>
            <li><strong>Subscriptions / entitlements:</strong> e.g., RevenueCat and app stores (Apple/Google).</li>
            <li><strong>Email/support tooling:</strong> systems that help us respond to support requests.</li>
        </ul>
        <p>If we add or change subprocessors, we'll update this policy.</p>
        <h2 className={styles.legalH2}>Your rights</h2>
        <p>Depending on your location, you may have rights to access, export, correct, or delete your personal data. You can contact us at <a href="mailto:corporate@deenroute.org">corporate@deenroute.org</a>.</p>
        <ul className={styles.legalList}>
            <li><strong>Access / export:</strong> request a copy of the data we hold about you.</li>
            <li><strong>Correction:</strong> ask us to fix inaccurate information.</li>
            <li><strong>Deletion:</strong> request account deletion and removal of associated personal data where applicable.</li>
        </ul>
        <p><strong>Delete account:</strong> If the option is available, you can delete your account in-app in Settings. Otherwise, email <a href="mailto:corporate@deenroute.org">corporate@deenroute.org</a> from the email associated with your account and include "Delete my account" in the subject.</p>
        <h2 className={styles.legalH2}>Children's privacy</h2>
        <p>DeenRoute is not intended for children under <strong>13</strong> without a parent/guardian. If you believe a child has provided personal information without appropriate consent, please contact us and we'll take appropriate steps to delete it.</p>
        <h2 className={styles.legalH2}>Contact</h2>
        <p>Email: <a href="mailto:corporate@deenroute.org">corporate@deenroute.org</a></p>
        <h2 className={styles.legalH2}>Changes to this policy</h2>
        <p>We may update this Privacy Policy from time to time. We'll update the "Last updated" date above and, where appropriate, notify you in-app or via email.</p>
        <div className={styles.legalCrossLinks}>
            <a href="/terms" className={styles.legalLink}>Read Terms &amp; Conditions</a>
        </div>
    </>
);

/* ── Terms content ────────────────────────────────── */
const TermsContent = () => (
    <>
        <p className={styles.legalIntro}>
            These Terms &amp; Conditions ("Terms") govern your use of DeenRoute (the "Service"), including our website
            and mobile experiences. By accessing or using the Service, you agree to these Terms.
        </p>
        <h2 className={styles.legalH2}>Company Information</h2>
        <p>
            DeenRoute is operated by <strong>DeenRoute Ltd</strong>, a company registered in England and Wales.
        </p>
        <h2 className={styles.legalH2}>Account terms</h2>
        <ul className={styles.legalList}>
            <li>You're responsible for keeping your account credentials secure and for activity under your account.</li>
            <li>You must provide accurate information and keep it up to date.</li>
            <li>We may suspend or terminate accounts that violate these Terms.</li>
        </ul>
        <h2 className={styles.legalH2}>Acceptable use</h2>
        <ul className={styles.legalList}>
            <li>Don't misuse the Service, attempt to access systems without authorization, or interfere with normal
                operation.
            </li>
            <li>Don't upload or transmit malicious code, spam, or harmful content.</li>
            <li>Don't reverse engineer the Service except where permitted by law.</li>
        </ul>
        <h2 className={styles.legalH2}>Educational content disclaimer</h2>
        <p>DeenRoute provides educational content for learning and revision. All data is verified by scholars, but is
            not a substitute for qualified scholars, local imams, or formal religious verdicts. Always consult trusted
            scholars for complex questions.</p>
        <h2 className={styles.legalH2}>Subscriptions and purchases</h2>
        <p>If the Service offers subscriptions or paid features, billing may be handled by Apple App Store, Google Play,
            or other providers. Your purchase is subject to the applicable provider terms and policies.</p>
        <ul className={styles.legalList}>
            <li><a href="https://www.apple.com/legal/internet-services/itunes/us/terms.html" target="_blank"
                   rel="noopener noreferrer">Apple Media Services Terms</a></li>
            <li><a href="https://play.google.com/intl/en_us/about/play-terms/" target="_blank"
                   rel="noopener noreferrer">Google Play Terms</a></li>
        </ul>
        <p>We don't make pricing promises on this page; prices, trials, and renewal rules are shown at checkout and may
            change.</p>
        <h2 className={styles.legalH2}>Limitation of liability</h2>
        <p>To the maximum extent permitted by law, DeenRoute is provided on an "as is" and "as available" basis. We
            aren't liable for indirect, incidental, special, consequential, or punitive damages, or any loss of data,
            profits, or revenues.</p>
        <h2 className={styles.legalH2}>Termination</h2>
        <p>You may stop using the Service at any time. We may suspend or terminate access if we believe you've violated
            these Terms, or if necessary to protect the Service, users, or third parties.</p>
        <h2 className={styles.legalH2}>Governing law</h2>
        <p>These Terms are governed by the laws of <strong>The Federal Republic of Nigeria</strong>, without regard to
            conflict of laws rules.</p>
        <h2 className={styles.legalH2}>Contact</h2>
        <p>Email: <a href="mailto:corporate@deenroute.org">corporate@deenroute.org</a></p>
        <div className={styles.legalCrossLinks}>
            <a href="/privacy" className={styles.legalLink}>Read Privacy Policy</a>
        </div>
    </>
);

/* ── Main component ───────────────────────────────── */
const Website = ({page}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    const isPrivacy = page === "privacy";
    const isTerms   = page === "terms";
    const isLegal   = isPrivacy || isTerms;

    /* Header shadow on scroll */
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useSeo({
        title: isPrivacy
            ? "Privacy Policy | DeenRoute"
            : isTerms
                ? "Terms & Conditions | DeenRoute"
                : "DeenRoute",
        description: isPrivacy
            ? "Read DeenRoute's Privacy Policy and learn how we collect, use, and protect your information."
            : isTerms
                ? "Read DeenRoute's Terms & Conditions for using the Service, including subscription and acceptable use terms."
                : "A daily companion in your Deen journey.",
    });

    const scrollToSection = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        setMenuOpen(false);
    };

    return (
        <div className={styles.container}>

            {/* ── Header ─────────────────────────────── */}
            <header className={`${styles.header} ${scrolled ? styles.headerScrolled : ''}`}>
                <div className={styles.headerContent}>
                    <a href="/" className={styles.logo} aria-label="DeenRoute home">
                        <img src={faviconIcon} alt="DeenRoute" className={styles.logoIcon} />
                        <span className={styles.brandName}>DeenRoute</span>
                    </a>

                    <nav className={styles.desktopNav} aria-label="Primary">
                        {!isLegal ? (
                            <>
                                <button onClick={() => scrollToSection("journey")} className={styles.navLink}>About</button>
                                <button onClick={() => scrollToSection("founder")} className={styles.navLink}>Founder</button>
                                <button onClick={() => scrollToSection("advisory")} className={styles.navLink}>Advisory Board</button>
                            </>
                        ) : (
                            <a href="/" className={styles.navLink}>Home</a>
                        )}
                        <a href="/app"     className={styles.navLink}>Download App</a>
                        <a href="/privacy" className={styles.navLink}>Privacy</a>
                        <a href="/terms"   className={styles.navLink}>Terms</a>
                        <a href="mailto:corporate@deenroute.org" className={`${styles.navLink} ${styles.contactLink}`} aria-label="Contact DeenRoute">
                            contact
                        </a>
                    </nav>

                    <button className={styles.mobileMenuButton} onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu" aria-expanded={menuOpen}>
                        <span className={`${styles.hamburger} ${menuOpen ? styles.hamburgerOpen : ''}`} aria-hidden="true" />
                    </button>
                </div>

                {menuOpen && (
                    <nav className={styles.mobileNav} aria-label="Mobile">
                        {!isLegal ? (
                            <>
                                <button onClick={() => scrollToSection("journey")}  className={styles.mobileNavLink}>About</button>
                                <button onClick={() => scrollToSection("founder")}  className={styles.mobileNavLink}>Founder</button>
                                <button onClick={() => scrollToSection("advisory")} className={styles.mobileNavLink}>Advisory Board</button>
                            </>
                        ) : (
                            <a href="/" className={styles.mobileNavLink} onClick={() => setMenuOpen(false)}>Home</a>
                        )}
                        <a href="/app"     className={styles.mobileNavLink} onClick={() => setMenuOpen(false)}>Download App</a>
                        <a href="/privacy" className={styles.mobileNavLink} onClick={() => setMenuOpen(false)}>Privacy</a>
                        <a href="/terms"   className={styles.mobileNavLink} onClick={() => setMenuOpen(false)}>Terms</a>
                        <a href="mailto:corporate@deenroute.org" onClick={() => setMenuOpen(false)}
                           className={`${styles.mobileNavLink} ${styles.contactLink}`} aria-label="Contact DeenRoute">
                            contact
                        </a>
                    </nav>
                )}
            </header>

            {/* ── Floating badge ─────────────────────── */}
            <FloatingStoreBadge />

            {/* ── Legal pages ────────────────────────── */}
            {isPrivacy && (
                <LegalPageShell title="Privacy Policy" lastUpdated={LAST_UPDATED_PRIVACY}>
                    <PrivacyPolicyContent />
                </LegalPageShell>
            )}
            {isTerms && (
                <LegalPageShell title="Terms & Conditions" lastUpdated={LAST_UPDATED_TERMS}>
                    <TermsContent />
                </LegalPageShell>
            )}

            {/* ── Main home page ─────────────────────── */}
            {!isLegal && (
                <main className={styles.content}>

                    {/* ── HERO ── */}
                    <section className={styles.hero}>
                        {/* Grain overlay */}
                        <div className={styles.heroGrain} aria-hidden="true" />
                        {/* Decorative circles */}
                        <div className={styles.heroOrb1} aria-hidden="true" />
                        <div className={styles.heroOrb2} aria-hidden="true" />

                        <div className={styles.heroContent}>
                            <div className={styles.heroTextSection}>
                                <motion.div
                                    initial="hidden"
                                    animate="visible"
                                    variants={fadeUp}
                                    transition={{duration: 0.9}}
                                    style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}
                                >
                                    <motion.span
                                        className={styles.heroEyebrow}
                                        variants={fadeUpDelayed(0.1)}
                                        initial="hidden"
                                        animate="visible"
                                    >
                                        Islamic Learning · Built for Today
                                    </motion.span>
                                    <motion.h1
                                        className={styles.heroTitle}
                                        variants={fadeUpDelayed(0.2)}
                                        initial="hidden"
                                        animate="visible"
                                    >
                                        A daily companion in&nbsp;your&nbsp;<span
                                        className={styles.heroTitleGold}>Deen&nbsp;journey</span>
                                    </motion.h1>
                                    <motion.p
                                        className={styles.heroSubtitle}
                                        variants={fadeUpDelayed(0.35)}
                                        initial="hidden"
                                        animate="visible"
                                    >
                                        Beginner-friendly, modern educational guide perfect for reverts new to the Deen
                                        and seasoned Muslims to refresh and expand their knowledge on Islam.
                                    </motion.p>
                                    <motion.blockquote
                                        className={styles.heroQuote}
                                        variants={fadeUpDelayed(0.5)}
                                        initial="hidden"
                                        animate="visible"
                                    >
                                        "And whoever is given knowledge is given a great good. And none will remember
                                        except those of understanding." — Surah Al-Baqarah (2:269)
                                    </motion.blockquote>
                                </motion.div>
                            </div>

                            <div className={styles.heroImageSection}>
                                <motion.div
                                    initial={{opacity: 0, scale: 0.94, y: 20}}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                    className={styles.heroImageWrapper}
                                >
                                    <img src="/assets/jamaldual.png" alt="DeenRoute app preview" className={styles.heroImage} />
                                </motion.div>
                            </div>
                        </div>

                        <ScrollArrow />
                    </section>

                    {/* ── IMPACT STRIP ── */}
                    <section className={styles.impactBanner}>
                        <div className={styles.impactContent}>
                            <span className={styles.impactIcon}>❤️</span>
                            <p className={styles.impactText}>
                                As DeenRoute grows, a portion of proceeds will support humanitarian relief in
                                Northern and Central Nigeria, Sudan, the DRC, and Palestine.
                            </p>
                        </div>
                    </section>

                    {/* ── MATW PARTNERSHIP / SUDAN EMERGENCY RELIEF ── */}
                    <section className={styles.reliefSection}>
                        <div className={styles.reliefPattern} aria-hidden="true" />
                        <motion.div
                            className={styles.reliefCard}
                            initial={{ opacity: 0, y: 28 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.7 }}
                        >
                            <div className={styles.reliefLogoWrap}>
                                <img
                                    src={matwLogo}
                                    alt="MATW Project — Muslims Around The World, 10 Years of Giving"
                                    className={styles.reliefLogo}
                                />
                            </div>
                            <div className={styles.reliefTextWrap}>
                                <span className={styles.sectionTag}>Emergency Relief</span>
                                <em className={styles.titleEmphasis}>In collaboration with MATW Project UK</em>
                                <p className={styles.bodyText}>
                                DeenRoute has partnered with MATW (Muslims Around The World) Project to provide
                                    emergency humanitarian relief for those affected by the crisis in Sudan. Your
                                    donation helps deliver food, shelter, and essential aid to families in need. - 1201110 (Charity Registration Number)
                                </p>

                                <a href="https://LaunchGood.com/EmergencyReliefSudan"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.reliefDonateBtn}
                                >
                                Donate — Sudan Emergency Relief
                            </a>
                        </div>
                    </motion.div>
                </section>

                    {/* ── APPROACH ── */}
                    <section id="journey" className={styles.approach}>

                        {/* Row 1 */}
                        <div className={styles.approachRow}>
                            <div className={styles.approachRowImage}>
                                <img src="/assets/onboardingsc.png" alt="DeenRoute app home screen" />
                            </div>
                            <div className={styles.approachRowText}>
                                <motion.div
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.3 }}
                                    variants={fadeUp}
                                    transition={{ duration: 0.7 }}
                                >
                                    <span className={styles.sectionTag}>Our Approach</span>
                                    <h2 className={styles.sectionTitle}>
                                        Modern UI meets <em className={styles.titleEmphasis}>authentic</em> Islamic content
                                    </h2>
                                    <p className={styles.bodyText}>
                                        We blend modern UI/UX with authentic Islamic content, backed by Quran and
                                        authentic Hadith references. Our diverse activities support various learning
                                        styles, keeping users engaged and encouraging deeper exploration.
                                    </p>
                                    <h3 className={styles.subsectionTitle}>Why We Built DeenRoute</h3>
                                    <p className={styles.bodyText}>
                                        DeenRoute addresses the challenge of digital information overload with a
                                        solution that is truly interactive. Built for reverts, children, and lifelong
                                        learners, our platform transforms Islamic education from a passive task into
                                        an engaging, personal, and rewarding adventure.
                                    </p>
                                </motion.div>
                            </div>
                        </div>

                        {/* Row 2 */}
                        <div className={styles.approachRow}>
                            <div className={styles.approachRowText}>
                                <motion.div
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.3 }}
                                    variants={fadeUp}
                                    transition={{ duration: 0.7 }}
                                >
                                    <span className={styles.sectionTag}>Our Vision</span>
                                    <h2 className={styles.sectionTitle}>
                                        Learning for Jannah, <em className={styles.titleEmphasis}>one lesson</em> at a time
                                    </h2>
                                    <p className={styles.bodyText}>
                                        DeenRoute empowers Muslims to navigate the digital dunya with confidence,
                                        anchoring their knowledge in Qur'an and authentic Hadith. By making Islamic
                                        learning intentional, engaging, and habit-forming, we help users grow in faith,
                                        protect their hearts from information overload, and build deeds through
                                        consistent knowledge-seeking.
                                    </p>
                                    <p className={styles.bodyText}>
                                        Our vision is a generation that learns with joy, lives with purpose, and
                                        progresses toward the ultimate success — Jannah in the Akhira.
                                    </p>
                                </motion.div>
                            </div>
                            <div className={styles.approachRowImage}>
                                <img src={appRight} alt="DeenRoute journey simulation" />
                            </div>
                        </div>

                    </section>

                    {/* ── FEATURES ── */}
                    <section className={styles.betaSection}>
                        {/* Decorative top wave */}
                        <div className={styles.betaWaveTop} aria-hidden="true" />

                        <div className={styles.betaSectionHeader}>
                            <span className={styles.sectionTag}>Features</span>
                            <h2 className={styles.sectionTitle}>Learn the Deen your way</h2>
                            <p className={styles.bodyText}>
                                Interactive tools designed to make every session short, meaningful, and memorable.
                            </p>
                        </div>

                        <div className={styles.betaContent}>
                            {/* Flashcards */}
                            <motion.div
                                className={styles.featureCard}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.6, delay: 0 }}
                            >
                                <div className={styles.featureImageWrap}>
                                    <img src={flashcardsImg} alt="Flashcards view" className={styles.featureImage} />
                                </div>
                                <p className={styles.featureText}>
                                    Modern games like flashcards help in memorising Surahs and understanding their meanings.
                                </p>
                            </motion.div>

                            {/* CTA centre */}
                            <motion.div
                                id="beta"
                                className={styles.betaForm}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.6, delay: 0.15 }}
                            >
                                <div className={styles.betaFormGlow} aria-hidden="true" />
                                <h3 className={styles.betaTitle}>DeenRoute is now live — download the app</h3>
                                <p className={styles.betaDescription}>
                                    Alhamdulillah, DeenRoute is now available on iOS and Android. Use the download
                                    badge at the bottom of the page to get the app and start short, daily, meaningful
                                    learning on the go.
                                </p>
                                <p className={styles.betaDescription}>
                                    If you'd like to know more or get involved, send us an email at{" "}
                                    <a href="mailto:corporate@deenroute.org">corporate@deenroute.org</a>.
                                </p>
                                <div className={styles.betaStoreBadges}>
                                    <a href={IOS_URL} target="_blank" rel="noopener noreferrer">
                                        <img src={appStoreBtn} alt="Download on the App Store" className={styles.betaStoreBtn} />
                                    </a>
                                    <a href={ANDROID_URL} target="_blank" rel="noopener noreferrer">
                                        <img src={playStoreBtn} alt="Get it on Google Play" className={styles.betaStoreBtn} />
                                    </a>
                                </div>
                            </motion.div>

                            {/* Group sessions */}
                            <motion.div
                                className={styles.featureCard}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                            >
                                <div className={styles.featureImageWrap}>
                                    <img src={groupSessionsImg} alt="Group sessions view" className={styles.featureImage} />
                                </div>
                                <p className={styles.featureText}>
                                    Group sessions implemented on mobile to encourage collective, interactive learning.
                                </p>
                            </motion.div>
                        </div>
                    </section>

                    {/* ── ADVISORY BOARD ── */}
                    <section id="advisory" className={styles.advisorySection}>
                        {/* Geometric pattern */}
                        <div className={styles.advisoryPattern} aria-hidden="true" />

                        <motion.div
                            className={styles.advisoryCard}
                            initial={{ opacity: 0, y: 28 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.7 }}
                        >
                            <span className={styles.sectionTag}>Advisory Board</span>
                            <h2 className={styles.advisoryTitle}>Our Advisors</h2>
                            <p className={styles.advisoryText}>
                                Mamoon Ismail is an experienced AI Engineer and venture strategist currently at FutureSight Ventures. With a deep background in venture capital and deep-tech, he has supported over 60 companies and facilitated partnerships with 300+ VC firms. Previously, he served as the Head of AI/CTO at Vanse.ai (acquired by Forum Ventures) and as a Senior Strategist at Rokk3r Labs, where he managed a $224M AUM portfolio.
                                A computer engineer and former Research Lead at NYU Langone, Mamoon has authored peer-reviewed research on machine learning and medical monitoring systems. His expertise in cross-border acceleration and product strategy is instrumental in scaling DeenRoute's mission to provide premium digital Islamic education to a global audience.
                            </p>
                        </motion.div>

                        <motion.div
                            className={`${styles.advisoryCard} ${styles.advisoryCardAlt}`}
                            initial={{ opacity: 0, y: 28 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.7, delay: 0.15 }}
                        >
                            <h2 className={styles.advisoryTitle}>Our Scholars</h2>
                            <p className={styles.advisoryText}>
                                Dr. Tajudeen Adebayo is an accomplished Islamic scholar and Shariʿah expert with a PhD
                                in Arabic and extensive experience in Islamic education, religious leadership, and public
                                enlightenment. He serves as the Imam of the Falomo Police Central Mosque, Lagos, and is
                                a Deputy Director of Programmes at the Voice of Nigeria. With strong grounding in
                                Qur'anic studies, Hadith, Fiqh, and contemporary Shariʿah issues, Dr. Adebayo provides
                                scholarly guidance that bridges classical Islamic knowledge and modern realities. His
                                background in broadcasting and community engagement equips him to support accurate,
                                ethical, and accessible Islamic content.
                            </p>
                        </motion.div>
                    </section>

                    {/* ── FOUNDER ── */}
                    <section id="founder" className={styles.founderSection}>
                        {/* Decorative arabesque SVG */}
                        <svg className={styles.founderDecor} aria-hidden="true" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 10 L120 80 L190 80 L135 125 L155 190 L100 150 L45 190 L65 125 L10 80 L80 80 Z"
                                  fill="none" stroke="rgba(11,95,63,0.07)" strokeWidth="1.5"/>
                            <circle cx="100" cy="100" r="70" fill="none" stroke="rgba(201,168,76,0.07)" strokeWidth="1"/>
                            <circle cx="100" cy="100" r="50" fill="none" stroke="rgba(11,95,63,0.05)" strokeWidth="1"/>
                        </svg>

                        <motion.div
                            className={styles.founderCard}
                            initial={{ opacity: 0, y: 28 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.8 }}
                        >
                            <span className={styles.sectionTag}>Founder Story</span>
                            <h2 className={styles.founderTitle}>Built from personal experience</h2>
                            <div className={styles.founderDivider} aria-hidden="true" />
                            <p className={styles.bodyText}>
                                I reverted to Islam seeking truth and clarity, and soon realized how difficult it can be
                                to learn the deen authentically in a world of digital noise, especially for reverts like
                                myself, Gen Z and children. That personal craving shaped the vision for DeenRoute. With
                                a background in software engineering and project leadership, including building
                                Sharia-aligned finance and education tools at Lotus Bank, I've seen how faith-driven
                                products can deliver real, everyday impact.
                            </p>
                            <p className={styles.bodyText}>
                                DeenRoute is my answer to that early experience — a platform built to make learning
                                Islam credible, structured, and joyful, supporting believers in a modern interactive
                                way, the digital era. One flashcard, one lesson at a time, DeenRoute digitises Islamic
                                learning for easy access and lasting impact.
                            </p>
                            <p className={styles.founderSignature}>— <strong>Ayaan</strong></p>
                        </motion.div>
                    </section>

                </main>
            )}

            {/* ── Footer ─────────────────────────────── */}
            <footer className={styles.footer}>
                <div className={styles.footerTopBorder} aria-hidden="true" />
                <div className={styles.footerContent}>
                    <div className={styles.footerLeft}>
                        <p className={styles.footerBrand}>DeenRoute</p>
                        <p className={styles.footerTagline}>Practical Deen learning — short, daily, meaningful.</p>
                        <p className={styles.footerCompany}>
                            © 2026 DeenRoute Ltd. Registered in England and Wales. Company No. 17207277.
                        </p>
                        <p className={styles.footerLegalLinks}>
                            <a href="/privacy" className={styles.footerLegalLink}>Privacy</a>
                            <span className={styles.footerLegalSeparator} aria-hidden="true">•</span>
                            <a href="/terms" className={styles.footerLegalLink}>Terms</a>
                        </p>
                    </div>
                    <div className={styles.footerRight}>
                        <a href="mailto:corporate@deenroute.org" className={styles.footerEmail}>
                            corporate@deenroute.org
                        </a>
                        <a href="https://linkedin.com/company/deenroute/" target="_blank" rel="noopener noreferrer"
                           className={styles.footerLink} aria-label="DeenRoute LinkedIn" title="LinkedIn">
                            <img src={linkedinIcon} alt="LinkedIn" className={styles.footerLinkIcon} />
                        </a>
                    </div>
                </div>
            </footer>

        </div>
    );
};

export default Website;
